import { ArrowRight, CheckCircle, MessageSquare, UserPlus, Zap } from 'lucide-react-native';
import type { ReactNode } from 'react';
import { StyleSheet, View } from 'react-native';
import { AnimatedCard, FadeIn } from '../../../components/animations';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { Card } from '../../../components/primitives/Card';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { darkPrecisionTypography } from '../../../theme/typography';

type ActivityType =
  | 'lead_created'
  | 'stage_changed'
  | 'note_added'
  | 'workflow_triggered'
  | 'created'
  | 'stage_change'
  | 'assigned'
  | 'email_sent';

export interface Activity {
  id: string;
  type: ActivityType;
  title: string;
  description: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

interface RecentActivityProps {
  activities: Activity[];
  onViewAll?: () => void;
  loading?: boolean;
}

const activityIcons: Record<ActivityType, ReactNode> = {
  lead_created: <UserPlus size={15} color={colors.metrics.visitors} strokeWidth={2} />,
  created: <UserPlus size={15} color={colors.metrics.visitors} strokeWidth={2} />,
  stage_changed: <ArrowRight size={15} color={colors.metrics.conversion} strokeWidth={2} />,
  stage_change: <ArrowRight size={15} color={colors.metrics.conversion} strokeWidth={2} />,
  note_added: <MessageSquare size={15} color={colors.status.info.main} strokeWidth={2} />,
  workflow_triggered: <Zap size={15} color={colors.metrics.campaigns} strokeWidth={2} />,
  assigned: <UserPlus size={15} color={colors.metrics.campaigns} strokeWidth={2} />,
  email_sent: <MessageSquare size={15} color={colors.metrics.visitors} strokeWidth={2} />,
};

const activityColors: Record<ActivityType, { bg: string; iconColor: string }> = {
  lead_created: {
    bg: colors.metrics.visitorsSoft,
    iconColor: colors.metrics.visitors,
  },
  created: {
    bg: colors.metrics.visitorsSoft,
    iconColor: colors.metrics.visitors,
  },
  stage_changed: {
    bg: colors.metrics.conversionSoft,
    iconColor: colors.metrics.conversion,
  },
  stage_change: {
    bg: colors.metrics.conversionSoft,
    iconColor: colors.metrics.conversion,
  },
  note_added: {
    bg: colors.status.info.bg,
    iconColor: colors.status.info.main,
  },
  workflow_triggered: {
    bg: colors.status.warning.bg,
    iconColor: colors.metrics.campaigns,
  },
  assigned: {
    bg: colors.status.warning.bg,
    iconColor: colors.metrics.campaigns,
  },
  email_sent: {
    bg: colors.metrics.visitorsSoft,
    iconColor: colors.metrics.visitors,
  },
};

function ActivityItem({
  activity,
  index,
  isLast,
}: Readonly<{
  activity: Activity;
  index: number;
  isLast: boolean;
}>) {
  const colorConfig = activityColors[activity.type];

  return (
    <FadeIn delay={200 + index * 80} translateY={8}>
      <HStack
        spacing="md"
        align="center"
        style={[styles.activityRow, isLast ? null : styles.activityRowDivider]}
      >
        <View
          style={[
            styles.activityIcon,
            {
              backgroundColor: colors.surface.row,
              borderWidth: 1,
              borderColor: colorConfig.iconColor,
            },
          ]}
        >
          {activityIcons[activity.type]}
        </View>

        <VStack spacing="none" style={styles.activityBody}>
          <Text
            color={colors.text.primary}
            style={[
              darkPrecisionTypography.heading,
              { fontSize: 13, lineHeight: 18, color: colors.text.primary },
            ]}
            numberOfLines={1}
          >
            {activity.title}
          </Text>
          <Text
            color={colors.text.secondary}
            numberOfLines={1}
            style={{ marginTop: 1, fontSize: 12, lineHeight: 17, color: colors.text.secondary }}
          >
            {activity.description}
          </Text>
        </VStack>

        <Text
          color={colors.text.tertiary}
          style={[
            darkPrecisionTypography.label,
            { fontSize: 10, letterSpacing: 0.6, color: colors.text.tertiary },
          ]}
        >
          {activity.timestamp}
        </Text>
      </HStack>
    </FadeIn>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <Card
      variant="default"
      radius="2xl"
      padding="xl"
      style={{
        minHeight: 180,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CheckCircle size={34} color={colors.text.tertiary} strokeWidth={1.5} />
      <Text
        variant="body"
        color={colors.text.secondary}
        style={{ marginTop: 12, textAlign: 'center' }}
      >
        {t('dashboard.recentActivity.empty.title')}
      </Text>
      <Text
        variant="caption"
        color={colors.text.tertiary}
        style={{ marginTop: 4, textAlign: 'center' }}
      >
        {t('dashboard.recentActivity.empty.description')}
      </Text>
    </Card>
  );
}

export function RecentActivity({ activities, onViewAll, loading }: Readonly<RecentActivityProps>) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <AnimatedCard delay={200}>
        <Card variant="default" radius="3xl" padding="lg" style={{ flex: 1, minHeight: 350 }}>
          <VStack spacing="lg">
            <HStack justify="between" align="center">
              <Text
                color={colors.text.primary}
                style={[darkPrecisionTypography.heading, { color: colors.text.primary }]}
              >
                {t('dashboard.recentActivity.title')}
              </Text>
            </HStack>
            <Box alignItems="center" justifyContent="center" paddingY="xl">
              <Text variant="body" color={colors.text.tertiary}>
                {t('dashboard.recentActivity.loading')}
              </Text>
            </Box>
          </VStack>
        </Card>
      </AnimatedCard>
    );
  }

  return (
    <AnimatedCard delay={200}>
      <Card variant="default" radius="3xl" padding="lg" style={{ flex: 1, minHeight: 350 }}>
        <VStack spacing="md" style={{ flex: 1 }}>
          <HStack justify="between" align="center">
            <Text
              color={colors.text.primary}
              style={[darkPrecisionTypography.heading, { color: colors.text.primary }]}
            >
              {t('dashboard.recentActivity.title')}
            </Text>
            {onViewAll && activities.length > 0 && (
              <Button
                testID="dashboard_activity_viewall_button"
                variant="ghost"
                size="sm"
                onPress={onViewAll}
              >
                {t('dashboard.recentActivity.viewAll')}
              </Button>
            )}
          </HStack>

          {activities.length === 0 ? (
            <EmptyState />
          ) : (
            <VStack spacing="none">
              {activities.map((activity, index) => (
                <ActivityItem
                  key={activity.id}
                  activity={activity}
                  index={index}
                  isLast={index === activities.length - 1}
                />
              ))}
            </VStack>
          )}
        </VStack>
      </Card>
    </AnimatedCard>
  );
}

const styles = StyleSheet.create({
  activityRow: {
    paddingVertical: 11,
  },
  activityRowDivider: {
    borderBottomWidth: 1,
    borderBottomColor: colors.border.subtle,
  },
  activityIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  activityBody: {
    flex: 1,
    minWidth: 0,
  },
});
